import React from 'react';

import Head from 'next/head';

import * as prismic from '@prismicio/client';
import { SliceZone } from '@prismicio/react';
import { withEventScope } from '@repo/event-processor';
import { Spacer } from '@repo/ui/components/Spacer';
import { themeStyle } from '@repo/ui/theming';
import { buildWebsiteUrl } from '@repo/utils/url';

import { ConfigurableSections } from 'components/HeadMenu/Header/constants';
import { LandingPageProps } from 'components/Landing/LandingPage/types';
import Layout from 'components/Layout/Layout';
import ScrollIndicator from 'components/ScrollIndicator/ScrollIndicator';
import { linkResolver } from 'prismicio';
import { components } from 'slices';

import { LandingPageContainer, LandingPageExtraLargeStyling } from './styles';

const HEADER_HIDDEN_SECTIONS = [
  ConfigurableSections.nav,
  ConfigurableSections.search,
  ConfigurableSections.projects,
  ConfigurableSections.pricing,
];

const LandingPage: React.FC<LandingPageProps> = ({
  document,
  headerHiddenSections = [],
  contextData,
}) => {
  const url = prismic.asLink(document, {
    linkResolver,
  });
  const { data } = document;
  const forceDarkModeContent = data.forceDarkModeContent ? 'dark' : undefined;
  const hasBackgroundGradient = data.hasBackgroundGradient ?? true;
  const backgroundGradient =
    data.backgroundGradient ?? themeStyle.varBackgroundGradientHalf;

  return (
    <Layout
      footerStyle={data.footerStyle}
      forceDarkModeContent={forceDarkModeContent}
      headerHiddenSections={
        data.onlyLogo
          ? HEADER_HIDDEN_SECTIONS.concat(headerHiddenSections)
          : headerHiddenSections
      }
    >
      <Head>
        {/* Facebook, Open Graph protocol */}
        <meta key="og:type" content="article" property="og:type" />
        <meta
          key="og:url"
          content={buildWebsiteUrl(url ?? '')}
          property="og:url"
        />
        {document.data.noindex && (
          <meta key="robots" content="noindex,nofollow" name="robots" />
        )}
      </Head>
      <LandingPageContainer
        backgroundGradient={backgroundGradient}
        className={`landing-page ${forceDarkModeContent}`}
        hasBackgroundGradient={hasBackgroundGradient}
      >
        <LandingPageExtraLargeStyling forceExtraLargeStyling={false}>
          <SliceZone
            components={components}
            //By using sliceZone's context we can pass parameters into our slices
            context={{
              ...contextData,
              forceDarkModeContent: data.forceDarkModeContent ?? false,
              forceExtraLargeStyling: data.forceExtraLargeStyling ?? false,
              pageId: document.uid,
            }}
            slices={document.data.slices}
          />
        </LandingPageExtraLargeStyling>
      </LandingPageContainer>
      <SliceZone components={components} slices={document.data.slices1} />
      <Spacer height="30px" />
      {document.data?.scrollIndicator && (
        <ScrollIndicator hideAfterScroll={true} scrollOnClick={true} />
      )}
    </Layout>
  );
};

export default withEventScope(LandingPage, { page: 'landingPage' });
